import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  collection,
  getDocs,
  query,
  where,
  limit,
} from "firebase/firestore";
import DOMPurify from "dompurify";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import StructuredData from "../components/StructuredData";
import { Container, Button } from "@mui/material";
import { CircularProgress } from "@mui/material";
import { findBlogBySlug, generateSlug } from "../utils/slug";
import { db } from "../lib/firebase";

const BlogDetail = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        setLoading(true);

        const slugQuery = query(
          collection(db, "blogs"),
          where("slug", "==", slug),
          limit(1)
        );
        const slugSnapshot = await getDocs(slugQuery);

        if (!slugSnapshot.empty) {
          const doc = slugSnapshot.docs[0];
          setBlog({ id: doc.id, ...doc.data() });
          return;
        }

        const allBlogsSnapshot = await getDocs(collection(db, "blogs"));
        const blogs = allBlogsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        const foundBlog = findBlogBySlug(blogs, slug);

        if (foundBlog) {
          setBlog(foundBlog);
        } else {
          navigate("/blog");
        }
      } catch (fetchError) {
        console.error("Error fetching blog details: ", fetchError);
        navigate("/blog");
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();
  }, [slug, navigate]);

  if (loading || !blog) {
    return (
      <div style={{ textAlign: "center", padding: "20px" }}>
        <CircularProgress />
      </div>
    );
  }

  const handleBackButtonClick = () => {
    navigate("/blog");
  };

  const getDescription = () => {
    if (blog.content) {
      const textContent = blog.content
        .replace(/<[^>]*>/g, "")
        .substring(0, 160);
      return textContent + "...";
    }
    return `Read ${blog.title} on EZ Car Rentals blog. Expert car rental tips and advice for Melbourne, Victoria, Australia.`;
  };

  const blogSlug = blog.slug || generateSlug(blog.title);
  const sanitizedContent = DOMPurify.sanitize(blog.content || "", {
    USE_PROFILES: { html: true },
  });
  const blogUrl = `https://ezcarrentals.com.au/blog/${blogSlug}`;
  const datePublished = blog.createDate
    ? new Date(blog.createDate.seconds * 1000).toISOString()
    : undefined;

  return (
    <>
      <SEO
        title={`${blog.title} | EZ Car Rentals Blog`}
        description={getDescription()}
        keywords={`${blog.title}, car rental Melbourne, vehicle hire Victoria, car rental tips Australia, ${blog.title} Melbourne`}
        url={blogUrl}
        type="article"
        image={blog.imageUrl || undefined}
      />
      <StructuredData
        type="article"
        article={{
          title: blog.title,
          description: getDescription(),
          image: blog.imageUrl,
          datePublished,
          dateModified: datePublished,
          url: blogUrl,
        }}
      />
      <Navbar />

      <div className='container '>
        <Container maxWidth='xl' sx={{ marginTop: 15, marginBottom: 10 }}>
          <Button
            variant='outlined'
            sx={{ mb: 3 }}
            onClick={handleBackButtonClick}
          >
            Back to blogs
          </Button>

          <div className='blog-details-main '>
            <div className='blog-detail'>
              <h1>{blog.title}</h1>
              <div className='blog-create-date'>
                {blog.createDate
                  ? new Date(blog.createDate.seconds * 1000).toDateString()
                  : "No Date"}
              </div>
              {blog.imageUrl && (
                <img
                  className='blog-detail-img'
                  src={blog.imageUrl}
                  alt={blog.title}
                />
              )}

              <div
                className='blog-content'
                dangerouslySetInnerHTML={{ __html: sanitizedContent }}
              />
            </div>
          </div>
        </Container>
      </div>
      <Footer />
    </>
  );
};

export default BlogDetail;
